import { apiHelpers } from './api';
import { API_CONFIG, AUTH_CONFIG } from '@/utils/constants';

// Media types
export type MediaCategory = 'EVENT' | 'PROFILE' | 'RECEIPT' | 'OTHER';

export interface MediaItem {
  id: string;
  uploadedBy: string;
  filename: string;
  originalName: string;
  mimeType: string;
  fileSize: number;
  url: string;
  thumbnailUrl?: string;
  width?: number;
  height?: number;
  category: MediaCategory;
  caption?: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  uploader?: {
    username: string;
    displayName?: string;
    avatarUrl?: string;
  };
}

export interface UpdateMediaItemInput {
  caption?: string;
  isPublic?: boolean;
  category?: MediaCategory;
}

export interface MediaStats {
  totalItems: number;
  totalSize: number;
  byCategory: {
    category: MediaCategory;
    count: number;
    size: number;
  }[];
}

/**
 * Upload a single media file
 */
export const uploadMedia = async (
  file: File,
  options?: {
    category?: MediaCategory;
    caption?: string;
    isPublic?: boolean;
  }
): Promise<MediaItem> => {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.category) formData.append('category', options.category);
  if (options?.caption) formData.append('caption', options.caption);
  if (options?.isPublic !== undefined)
    formData.append('isPublic', String(options.isPublic));

  const response = await fetch(`${API_CONFIG.BASE_URL}/media/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${localStorage.getItem(AUTH_CONFIG.TOKEN_KEY)}`,
    },
    body: formData,
  });

  if (!response.ok) {
    throw new Error('Failed to upload media');
  }

  const data = await response.json();
  return data.data;
};

/**
 * Upload multiple media files
 */
export const uploadMultipleMedia = async (
  files: File[],
  options?: {
    category?: MediaCategory;
    isPublic?: boolean;
  }
): Promise<MediaItem[]> => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  if (options?.category) formData.append('category', options.category);
  if (options?.isPublic !== undefined)
    formData.append('isPublic', String(options.isPublic));

  const response = await fetch(
    `${API_CONFIG.BASE_URL}/media/upload/multiple`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem(AUTH_CONFIG.TOKEN_KEY)}`,
      },
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error('Failed to upload media');
  }

  const data = await response.json();
  return data.data;
};

/**
 * Get all media items with optional filters
 */
export const getMediaItems = async (
  uploadedBy?: string,
  category?: MediaCategory,
  limit: number = 50,
  offset: number = 0
): Promise<MediaItem[]> => {
  const params = new URLSearchParams();
  if (uploadedBy) params.append('uploadedBy', uploadedBy);
  if (category) params.append('category', category);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());

  return apiHelpers.get<MediaItem[]>(`/media?${params.toString()}`);
};

/**
 * Get a specific media item by ID
 */
export const getMediaItemById = async (mediaId: string): Promise<MediaItem> => {
  return apiHelpers.get<MediaItem>(`/media/${mediaId}`);
};

/**
 * Update a media item
 */
export const updateMediaItem = async (
  mediaId: string,
  data: UpdateMediaItemInput
): Promise<MediaItem> => {
  return apiHelpers.put<MediaItem>(`/media/${mediaId}`, data);
};

/**
 * Delete a media item
 */
export const deleteMediaItem = async (mediaId: string): Promise<void> => {
  await apiHelpers.delete(`/media/${mediaId}`);
};

/**
 * Add media to an event
 */
export const addMediaToEvent = async (
  mediaId: string,
  eventId: string
): Promise<void> => {
  await apiHelpers.post(`/media/${mediaId}/events/${eventId}`, {});
};

/**
 * Remove media from an event
 */
export const removeMediaFromEvent = async (
  mediaId: string,
  eventId: string
): Promise<void> => {
  await apiHelpers.delete(`/media/${mediaId}/events/${eventId}`);
};

/**
 * Get media for an event
 */
export const getEventMedia = async (eventId: string): Promise<MediaItem[]> => {
  return apiHelpers.get<MediaItem[]>(`/events/${eventId}/media`);
};

/**
 * Get media statistics
 */
export const getMediaStats = async (uploadedBy?: string): Promise<MediaStats> => {
  const params = uploadedBy ? `?uploadedBy=${uploadedBy}` : '';
  return apiHelpers.get<MediaStats>(`/media/stats${params}`);
};
